import React, { useState, useCallback } from 'react';
import { Input, Button, Spin, message } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { 
    fetchCartItemsCount, 
    fetchLikedProductCount, 
    fetchLoginUser 
} from '../../services';
import { setError } from '../../redux/slices/userSlice';
import GoogleAuthButton from '../googleAuthButton';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const ModalLogin: React.FC<{ onCancel: () => void; }> = ({ onCancel }) => {
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector((state) => state.user);
    const [data, setData] = useState({ email: '', password: '' });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setData(prevData => ({ ...prevData, [name]: value }));
        if (error) dispatch(setError(null));
    };

    const handleLogin = useCallback(async () => {
        const email = data.email.trim().toLowerCase();

        dispatch(setError(null));

        if (!email || !data.password.trim()) {
            message.error('Please enter your email and password');
            return;
        }

        if (!emailRegex.test(email)) {
            message.error('Please enter a valid email address');
            return;
        }

        try {
            const user = await dispatch(fetchLoginUser({
                email,
                password: data.password,
            })).unwrap();

            if (user?.id) {
                dispatch(fetchCartItemsCount(user.id));
                dispatch(fetchLikedProductCount(user.id));
            }

            onCancel();
            setData({ email: '', password: '' });
        } catch (error: any) {
            message.error(error.message || 'Login failed');
        }
    }, [data, dispatch, onCancel]);

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
                void handleLogin();
            }}
        >
            <label className="input-label" htmlFor="login-email">Enter your email</label>
            <Input
                id="login-email"
                placeholder="Email"
                name="email"
                value={data.email}
                onChange={handleChange}
                style={{ marginBottom: '10px' }}
            />
            <label className="input-label" htmlFor="login-password">Enter your Password</label>
            <Input.Password
                id="login-password"
                placeholder="Password"
                name="password"
                value={data.password}
                onChange={handleChange}
                style={{ marginBottom: '10px' }}
            />
            <Button
                type="primary"
                htmlType="submit"
                disabled={loading}
                aria-label={loading ? 'Logging in' : 'Login'}
                aria-busy={loading}
            >
                {loading ? <Spin size="small" aria-hidden="true" /> : 'Login'}
            </Button>
            <div style={{ margin: '16px 0', textAlign: 'center', color: '#8c8c8c' }}>or</div>
            <GoogleAuthButton />
            {error && <p style={{ color: 'red' }}>{error}</p>}
        </form>
    );
};

export default ModalLogin;
